import { ApiError, apiRequest } from "./apiClient";
import type { GameRuleWizardField } from "../types/gameRuleTemplate";
import { mapFieldsJsonToWizardFields } from "../utils/gameRuleFields";

/** Must match {@link GameRuleGeneratePage} session bootstrap. */
export const generationSessionStorageKey = (sessionId: string): string => `gr-gen-v1:${sessionId}`;

type SessionCreateResponse = {
  readonly success: boolean;
  readonly data: {
    readonly game_rule_generation_session_id: string;
    readonly game_rule_template_id: string;
    readonly draft_stored_file_id: string;
    readonly document_web_url: string | null;
    readonly fields_json: unknown[];
  };
};

export type GenerationSessionPayload = {
  readonly sessionId: string;
  readonly templateId: string;
  readonly draftStoredFileId: string;
  readonly fields: GameRuleWizardField[];
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const o = JSON.parse(raw) as { detail?: string };
    return typeof o.detail === "string" ? o.detail : raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

export const createGameRuleGenerationSession = async (
  templateId: string
): Promise<GenerationSessionPayload> => {
  const response = await apiRequest(
    `/game-rule-templates/${encodeURIComponent(templateId)}/generation-sessions`,
    { method: "POST" }
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SessionCreateResponse;
  const d = json.data;
  const fj = Array.isArray(d.fields_json) ? d.fields_json : [];
  const fieldsJson = fj.filter(
    (x): x is Record<string, unknown> => x !== null && typeof x === "object" && !Array.isArray(x)
  );
  return {
    sessionId: String(d.game_rule_generation_session_id),
    templateId: String(d.game_rule_template_id),
    draftStoredFileId: String(d.draft_stored_file_id),
    fields: mapFieldsJsonToWizardFields(fieldsJson)
  };
};

export const putGameRuleGenerationSessionDocument = async (sessionId: string, blob: Blob): Promise<void> => {
  const fd = new FormData();
  fd.append("file", blob, "document.docx");
  const response = await apiRequest(
    `/game-rule-generation-sessions/${encodeURIComponent(sessionId)}/document`,
    { method: "PUT", body: fd }
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  await response.json();
};

export const commitGameRuleGenerationSession = async (
  sessionId: string,
  documentName: string
): Promise<void> => {
  const response = await apiRequest(
    `/game-rule-generation-sessions/${encodeURIComponent(sessionId)}/commit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_name: documentName.trim() })
    }
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  await response.json();
};
